import { BlockType, PageType, RelationshipType } from '@common/entities';
import { ApplicationForm } from 'state/slices/leadProfileSlice';

import { AnyPage } from 'types/page';

interface Element {
  id: string;
  name: string;
  type: string;
  label?: string;
  customType?: string;
}

// Blocks to exclude for the lead profile.
const BLOCK_NAMES_TO_EXCLUDE = ['coapplicant_block', 'guarantors'];

/**
 * Whether a page is configured to be hidden from the given relationship type via the
 * page-level hide flags.
 */
function isPageHiddenForRelationship(page: AnyPage, relationshipType: RelationshipType): boolean {
  return Boolean(
    page.configuration &&
      ((page.configuration.hideFromCoApplicant &&
        relationshipType === RelationshipType.Coapplicant) ||
        (page.configuration.hideFromGuarantor && relationshipType === RelationshipType.Guarantor)),
  );
}

export function filterApplicationPages(
  applicationForm: ApplicationForm | null,
  relationshipType: RelationshipType,
  hasDueNowCharges: boolean | undefined,
  isLeadProfile = false,
  isNonResponsibleOccupant = false,
): AnyPage[] {
  if (!applicationForm || !applicationForm.pages) {
    return [];
  }

  // First, filter the pages based on the relationshipType and page type.
  const filteredPages = applicationForm.pages.filter((page) => {
    // Floor Plans must remain completely hidden from secondary applicants,
    // regardless of page configuration or added content.
    const isSecondaryApplicant =
      relationshipType === RelationshipType.Coapplicant ||
      relationshipType === RelationshipType.Guarantor;
    if (page.type === PageType.FloorPlans && isSecondaryApplicant) {
      return false;
    }

    const shouldHide = isPageHiddenForRelationship(page, relationshipType);

    // Keep Payment visible until charges have loaded; false means no payment is due.
    const shouldHidePayment =
      page.type === PageType.Payment && hasDueNowCharges === false && !isLeadProfile;

    return page.type !== PageType.ThankYou && !shouldHide && !shouldHidePayment;
  });

  // Now, filter out specific blocks within the sections of each page.
  // Only show the page if there are any elements left to show.
  const cleanedPages = filteredPages
    .map((page) => {
      if (page.type === PageType.Blank) {
        const newSections = page.sections
          .map((section: any) => {
            const newElements = section.elements.filter((element: Element) => {
              if (isNonResponsibleOccupant) {
                // Strip out everything except Personal Information block
                return element.customType === BlockType.PersonalInformation;
              }
              if (isLeadProfile) {
                return !BLOCK_NAMES_TO_EXCLUDE.includes(element.name);
              }
              return true;
            });
            return { ...section, elements: newElements };
          })
          .filter((section: any) => section.elements.length > 0);

        if (newSections.length > 0) {
          return { ...page, sections: newSections };
        }
        return null;
      }

      // If it's a non-blank page (e.g. Floor Plans, Options) and the applicant is non-responsible, hide it
      if (isNonResponsibleOccupant) {
        return null;
      }

      return page;
    })
    .filter((page) => page !== null);

  // Finally, sort the pages by sequence.
  return cleanedPages.sort((a, b) => a.sequence - b.sequence) as AnyPage[];
}
